import React from 'react'
import orderBy from 'lodash/orderBy'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, makeStyles, Button, Typography, CircularProgress } from 'transfix-ui/core'
import { IConfigurableBuilderTest, IConfigurableBuilderTestResult } from 'types/graphqlTypes'
import { TestResultTag } from './TestResultTag'

interface ITestListModalProps extends Omit<IModalBaseProps, 'modalType'> {
  allTests: IConfigurableBuilderTest[]
  currentBuilderTest: IConfigurableBuilderTest
  latestTestResults?: IConfigurableBuilderTestResult[]
  onCancel: () => void
  runAllTestsHandler: () => void
  setCurrentBuilderTest: (currentBuilderTest: IConfigurableBuilderTest) => void
  showRunAllTestsButton: boolean
  testsAreRunning: boolean
}

const useTestListModalStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
  },
  spinner: {
    marginRight: theme.spacing(1),
  },
  selectTestButton: {
    marginLeft: theme.spacing(1),
  },
}))

export const TestListModal = ({
  allTests,
  currentBuilderTest,
  latestTestResults,
  onCancel,
  open = true,
  runAllTestsHandler,
  setCurrentBuilderTest,
  showRunAllTestsButton,
  testsAreRunning,
  ...props
}: ITestListModalProps) => {
  const classes = useTestListModalStyles()

  const defaultActions: IModalBaseProps['actions'] = [
    {
      label: 'Close',
      action: () => {
        onCancel()
      },
      type: 'dismiss',
      dataTest: 'close-button',
    },
  ]

  const latestResultForTest = (testId: string): IConfigurableBuilderTestResult | undefined => {
    return latestTestResults?.find(element => element.id === testId)
  }

  return (
    <ModalBase
      dataTest='TestListModal'
      modalType={MODAL_TYPES.form}
      title='Builder Tests'
      size={6}
      open={open}
      actions={defaultActions}
      {...props}>
      <Box>
        <Box className={classes.header}>
          {showRunAllTestsButton ? (
            <Button onClick={runAllTestsHandler}>Run All Tests</Button>
          ) : (
            <Typography>Load a builder version in order to run tests.</Typography>
          )}
          {testsAreRunning && <CircularProgress className={classes.spinner} />}
        </Box>
        {orderBy(allTests, 'name').map(item => (
          <Box
            mt={2}
            border='1px solid #d9d9d9'
            padding={2}
            key={item.id}
            display='flex'
            flexDirection='row'
            flexWrap='wrap'
            alignItems='center'
            justifyContent='space-between'>
            <Typography variant='body1'>{item.name}</Typography>
            {item.id === currentBuilderTest.id && <Typography>currently selected</Typography>}
            <Box>
              <TestResultTag status={latestResultForTest(item.id)?.status} />
              <Button
                className={classes.selectTestButton}
                onClick={() => {
                  setCurrentBuilderTest(item)
                  onCancel()
                }}>
                Select
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </ModalBase>
  )
}
